import { useEffect, useRef } from 'react';
import { useVisibilityChange } from '@uidotdev/usehooks';

/**
 * Minimal shape of WakeLockSentinel used by this hook. Avoids depending on
 * the global TypeScript `WakeLockSentinel` type (which is in lib.dom.d.ts
 * but missing from ESLint's `globals` browser list, surfacing a spurious
 * no-undef error).
 */
type WakeLockHandle = {
  readonly released: boolean;
  release(): Promise<void>;
};

/**
 * Hold a screen wake lock while `active` is true and the page is visible.
 *
 * Used by the audio recorder so an in-progress consult doesn't get cut off
 * when the phone screen turns off. iOS Safari suspends MediaRecorder when
 * the page is backgrounded; keeping the screen awake during recording
 * prevents that and avoids surprising data loss.
 *
 * The browser automatically releases the wake lock on visibility change
 * (this is part of the Wake Lock API spec, not a quirk), so this hook
 * re-requests the lock whenever the page becomes visible again while
 * `active` is still true.
 *
 * Graceful degradation: silently no-ops in browsers without the Wake Lock
 * API or when the user denies the request. The recording still works; the
 * screen just sleeps as usual.
 */
export function useScreenWakeLock(active: boolean): void {
  const isVisible = useVisibilityChange();
  const sentinelRef = useRef<WakeLockHandle | null>(null);

  useEffect(() => {
    if (!active || !isVisible) {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      // Release is async but we don't need to wait — the sentinel is
      // already detached. Swallow errors (release after page unload, etc).
      sentinel?.release().catch(() => undefined);
      return;
    }

    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const sentinel = (await navigator.wakeLock.request('screen')) as WakeLockHandle;
        if (cancelled) {
          await sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
      } catch {
        // Permission denied, transient failure, or page already hidden by
        // the time we resolved. Silent — recording proceeds without the
        // lock; the OS may sleep the screen normally.
      }
    })();

    return () => {
      // Mark the IIFE as cancelled (handles the in-flight request case) AND
      // release any sentinel that was already stored from this effect run.
      // Without the synchronous release, the sentinel would only be released
      // on the NEXT effect run via the early-return branch above — a window
      // wide enough to leak under rapid visibility toggles.
      cancelled = true;
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      sentinel?.release().catch(() => undefined);
    };
  }, [active, isVisible]);
}
